import * as React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from "@nfp-tracker/components";
import { cn, getTodayKey, isUpcomingCycle } from "@nfp-tracker/utils";
import { ChartFullscreenDialog, FullscreenButton } from "./ChartFullscreenDialog";
import { OverlayCycleChart, SeriesSwatch } from "./CycleChartCanvas";
import { HEADER_HEIGHT } from "./cycleChartStyles";
import {
  OVERLAY_ALIGN_LABELS,
  OVERLAY_EXCLUDED_TEXT,
  buildCycleOptions,
  buildOverlaySeries,
  overlayColumnCount,
  type ChartCycle,
  type ChartObservation,
  type OverlayAlign,
  type OverlayExcludedReason,
} from "./cycleChartData";
import { fitColumnWidth, useElementSize } from "./useChartLayout";

export interface CompareCyclesCardProps {
  /** Newest first, as the observations cache returns them. */
  cycles: ChartCycle[];
  observations: ChartObservation[];
  /** Today's date key in the owner's timezone; upcoming (pre-logged) cycles are left out. */
  today?: string;
  /** False for the ~13-month cache (see CycleChartCard). */
  hasFullHistory?: boolean;
  className?: string;
}

const COMPARE_COUNTS = [2, 3, 4, 6];
const TEMPERATURE_HEIGHT = 220;
const SCROLLBAR_ALLOWANCE = 14;

const ALIGN_DESCRIPTIONS: Record<OverlayAlign, string> = {
  cd1: "Waking temperatures from recent cycles, by cycle day.",
  peak: "Waking temperatures lined up on each Peak Day (P).",
  shift: "Waking temperatures lined up on each temp shift (S).",
};

export const CompareCyclesCard = ({
  cycles: allCycles,
  observations,
  today: todayProp,
  hasFullHistory = false,
  className,
}: CompareCyclesCardProps) => {
  const today = todayProp ?? getTodayKey();
  // Current + past only: a cycle that hasn't started has no temperatures worth comparing yet.
  const cycles = React.useMemo(
    () => allCycles.filter((cycle) => !isUpcomingCycle(cycle, today)),
    [allCycles, today]
  );
  const [count, setCount] = React.useState(3);
  const [align, setAlign] = React.useState<OverlayAlign>("cd1");
  const [hiddenIds, setHiddenIds] = React.useState<Set<string>>(() => new Set());
  const [fullscreen, setFullscreen] = React.useState(false);
  const [inlineRef, inlineSize] = useElementSize<HTMLDivElement>();

  const countOptions = COMPARE_COUNTS.filter((option) => option <= cycles.length);
  if (!countOptions.length) countOptions.push(Math.max(1, cycles.length));
  const effectiveCount = countOptions.includes(count) ? count : countOptions[countOptions.length - 1];

  const labelById = React.useMemo(
    () => new Map(buildCycleOptions(cycles, observations, today, { hasFullHistory }).map((option) => [option.id, option.label])),
    [cycles, observations, today, hasFullHistory]
  );
  const compared = React.useMemo(() => cycles.slice(0, effectiveCount), [cycles, effectiveCount]);
  const overlay = React.useMemo(
    () =>
      buildOverlaySeries(
        compared.map((cycle) => ({
          cycle,
          observations: observations.filter((obs) => obs.cycle_id === cycle.id),
          label: labelById.get(cycle.id) ?? cycle.start_date,
        })),
        align
      ),
    [compared, observations, labelById, align]
  );
  const colorIndexById = React.useMemo(
    () => new Map(compared.map((cycle, index) => [cycle.id, index])),
    [compared]
  );

  const excluded = overlay.series.filter((series) => series.excluded);
  const excludedText = (reason: OverlayExcludedReason) =>
    reason === "start-not-recorded" && !hasFullHistory
      ? "start not in loaded history"
      : OVERLAY_EXCLUDED_TEXT[reason];
  const hasVisible = overlay.series.some(
    (series) => !series.excluded && !hiddenIds.has(series.cycleId) && series.points.length
  );
  const emptyText =
    align === "peak" && excluded.length === overlay.series.length
      ? "None of these cycles has a Peak Day identified under your methods yet. Try lining them up on the temperature shift instead."
      : align === "shift" && excluded.length === overlay.series.length
        ? "No temperature shift has been detected in these cycles yet."
        : hasVisible
          ? ""
          : "No temperatures to compare. Turn a cycle back on above.";
  const columns = overlayColumnCount(overlay);

  const toggleSeries = (id: string) =>
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={String(effectiveCount)} onValueChange={(value) => setCount(Number(value))}>
        <SelectTrigger aria-label="Number of cycles to compare" className="h-9 w-auto min-w-[9rem]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {countOptions.map((option) => (
            <SelectItem key={option} value={String(option)}>
              Last {option} cycles
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ToggleGroup
        type="single"
        value={align}
        onValueChange={(value) => value && setAlign(value as OverlayAlign)}
        aria-label="Line cycles up on"
        className="rounded-md border p-0.5"
      >
        {(Object.keys(OVERLAY_ALIGN_LABELS) as OverlayAlign[]).map((key) => (
          <ToggleGroupItem key={key} value={key} size="sm" className="h-8 px-2.5 text-xs">
            {OVERLAY_ALIGN_LABELS[key]}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );

  // Fixed two-part legend: chips, then one reserved line for anything that can't be placed,
  // so switching alignment never changes the card's height.
  const legend = (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Cycles shown">
        {overlay.series.map((series) => {
          const shown = !series.excluded && !hiddenIds.has(series.cycleId);
          return (
            <button
              key={series.cycleId}
              type="button"
              disabled={series.excluded}
              aria-pressed={shown}
              onClick={() => toggleSeries(series.cycleId)}
              title={series.excluded && series.excludedReason ? excludedText(series.excludedReason) : undefined}
              className={cn(
                "inline-flex min-h-8 items-center gap-1.5 rounded border px-2 py-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                shown ? "border-border bg-muted/50 text-foreground" : "border-border/40 text-muted-foreground/60",
                series.excluded && "cursor-not-allowed line-through"
              )}
            >
              <SeriesSwatch index={colorIndexById.get(series.cycleId) ?? 0} />
              {series.label}
            </button>
          );
        })}
      </div>
      <p className="h-4 truncate text-[11px] leading-4 text-muted-foreground" aria-live="polite">
        {excluded.length
          ? `Not shown: ${excluded
              .map((series) => `${series.label} (${excludedText(series.excludedReason!)})`)
              .join("; ")}`
          : ""}
      </p>
    </div>
  );

  return (
    <Card className={cn("min-w-0", className)}>
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <CardTitle>Compare Cycles</CardTitle>
            <CardDescription>{ALIGN_DESCRIPTIONS[align]}</CardDescription>
          </div>
          <FullscreenButton onClick={() => setFullscreen(true)} label="Open cycle comparison full screen" />
        </div>
        {controls}
      </CardHeader>
      <CardContent className="min-w-0 space-y-3">
        {legend}
        <div ref={inlineRef} className="min-w-0">
          {/* Stays mounted under the full-screen dialog so the page behind it doesn't reflow. */}
          <OverlayCycleChart
            overlay={overlay}
            emptyText={emptyText}
            colorIndexById={colorIndexById}
            hiddenIds={hiddenIds}
            columnWidth={fitColumnWidth(inlineSize.width, columns, false)}
            temperatureHeight={TEMPERATURE_HEIGHT}
          />
        </div>
      </CardContent>

      <ChartFullscreenDialog
        open={fullscreen}
        onOpenChange={setFullscreen}
        title="Compare Cycles"
        description={ALIGN_DESCRIPTIONS[align]}
        controls={controls}
        toolbar={legend}
      >
        {(size) => (
          <OverlayCycleChart
            overlay={overlay}
            emptyText={emptyText}
            colorIndexById={colorIndexById}
            hiddenIds={hiddenIds}
            columnWidth={fitColumnWidth(size.width, columns, true)}
            temperatureHeight={Math.max(160, Math.floor(size.height - HEADER_HEIGHT - SCROLLBAR_ALLOWANCE))}
          />
        )}
      </ChartFullscreenDialog>
    </Card>
  );
};
